import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ProfileLocationPrompt } from '@/components/ProfileLocationPrompt'
import { useLDIdentify } from '@/hooks/useLDIdentify'

// Eagerly loaded — critical path (landing + auth)
import { Landing } from '@/pages/Landing'
import { Login } from '@/pages/Login'
import { NotFound } from '@/pages/NotFound'

// Lazily loaded — split into separate chunks
const Dashboard              = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Calendar               = lazy(() => import('@/pages/Calendar').then(m => ({ default: m.Calendar })))
const Catalog                = lazy(() => import('@/pages/Catalog').then(m => ({ default: m.Catalog })))
const Conferences             = lazy(() => import('@/pages/Conferences').then(m => ({ default: m.Conferences })))
const ConferenceDetail       = lazy(() => import('@/pages/ConferenceDetail').then(m => ({ default: m.ConferenceDetail })))
const HybridEvents           = lazy(() => import('@/pages/HybridEvents').then(m => ({ default: m.HybridEvents })))
const HybridEventDetail      = lazy(() => import('@/pages/HybridEventDetail').then(m => ({ default: m.HybridEventDetail })))
const Ondemand               = lazy(() => import('@/pages/Ondemand').then(m => ({ default: m.Ondemand })))
const WorkforceDevelopmentWednesday = lazy(() => import('@/pages/WorkforceDevelopmentWednesday').then(m => ({ default: m.WorkforceDevelopmentWednesday })))
const InternalSessions       = lazy(() => import('@/pages/InternalSessions').then(m => ({ default: m.InternalSessions })))
const SessionDetail          = lazy(() => import('@/pages/SessionDetail').then(m => ({ default: m.SessionDetail })))
const Cart                   = lazy(() => import('@/pages/Cart').then(m => ({ default: m.Cart })))
const Checkout               = lazy(() => import('@/pages/Checkout').then(m => ({ default: m.Checkout })))
const RegistrationHistory    = lazy(() => import('@/pages/RegistrationHistory').then(m => ({ default: m.RegistrationHistory })))
const SessionEvaluation      = lazy(() => import('@/pages/SessionEvaluation').then(m => ({ default: m.SessionEvaluation })))
const UserProfile            = lazy(() => import('@/pages/UserProfile').then(m => ({ default: m.UserProfile })))
const Transcript             = lazy(() => import('@/pages/Transcript').then(m => ({ default: m.Transcript })))
const Users                  = lazy(() => import('@/pages/Users').then(m => ({ default: m.Users })))
const Settings               = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })))
const Community              = lazy(() => import('@/pages/Community').then(m => ({ default: m.Community })))
const EmailCheck             = lazy(() => import('@/pages/EmailCheck').then(m => ({ default: m.EmailCheck })))
const Register               = lazy(() => import('@/pages/Register').then(m => ({ default: m.Register })))
const VerifyEmail            = lazy(() => import('@/pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })))
const ForgotPassword         = lazy(() => import('@/pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })))
const ResetPassword          = lazy(() => import('@/pages/ResetPassword').then(m => ({ default: m.ResetPassword })))
const AccountSetup           = lazy(() => import('@/pages/AccountSetup').then(m => ({ default: m.AccountSetup })))
const LoginCallback          = lazy(() => import('@/pages/LoginCallback').then(m => ({ default: m.LoginCallback })))
const EmployeeSSOCallback    = lazy(() => import('@/pages/EmployeeSSOCallback').then(m => ({ default: m.EmployeeSSOCallback })))
const CodeCheckIn            = lazy(() => import('@/pages/CodeCheckIn').then(m => ({ default: m.CodeCheckIn })))
const WalkInRegistration     = lazy(() => import('@/pages/WalkInRegistration').then(m => ({ default: m.WalkInRegistration })))

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void
  }
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-primary)', borderTopColor: 'transparent' }} />
    </div>
  )
}

function App() {
  const location = useLocation()
  const { enableCommunity = true } = useFlags()
  useLDIdentify()

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-B0JGZ07S21', {
        page_path: location.pathname.includes('/walk-in') ? location.pathname : location.pathname + location.search,
      })
    }
  }, [location])

  return (
    <ErrorBoundary>
    <Suspense key={location.pathname} fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/email-check" element={<EmailCheck />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account-setup" element={<AccountSetup />} />
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/auth/employee-sso" element={<EmployeeSSOCallback />} />
        <Route path="/retail" element={<Dashboard />} />
        <Route path="/" element={<Layout><Landing /></Layout>} />
        <Route path="/dashboard" element={<Layout><Landing /></Layout>} />
        <Route path="/calendar" element={<Layout><Calendar /></Layout>} />
        <Route path="/catalog" element={<Layout><Catalog /></Layout>} />
        <Route path="/conferences" element={<Layout><Conferences /></Layout>} />
        <Route path="/conferences/:eventId/sessions/:sessionId" element={<Layout><SessionDetail /></Layout>} />
        <Route path="/conferences/:eventId" element={<Layout><ConferenceDetail /></Layout>} />
        <Route path="/hybrid-events" element={<Layout><HybridEvents /></Layout>} />
        <Route path="/hybrid-events/:eventId/sessions/:sessionId" element={<Layout><SessionDetail /></Layout>} />
        <Route path="/hybrid-events/:eventId" element={<Layout><HybridEventDetail /></Layout>} />
        <Route path="/ondemand" element={<Layout><Ondemand /></Layout>} />
        <Route path="/workforce-development-wednesday" element={<Layout><WorkforceDevelopmentWednesday /></Layout>} />
        <Route path="/internal-sessions" element={<Layout><InternalSessions /></Layout>} />
        <Route path="/session/:sessionId" element={<Layout><SessionDetail /></Layout>} />
        <Route path="/session/:sessionId/walk-in" element={<WalkInRegistration />} />
        <Route path="/cart" element={<ProtectedRoute><Layout><Cart /></Layout></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Layout><Checkout /></Layout></ProtectedRoute>} />
        <Route path="/registration-history" element={<ProtectedRoute><Layout><RegistrationHistory /></Layout></ProtectedRoute>} />
        <Route path="/check-in" element={<ProtectedRoute><Layout><CodeCheckIn /></Layout></ProtectedRoute>} />
        <Route path="/session-evaluation" element={<ProtectedRoute><Layout><SessionEvaluation /></Layout></ProtectedRoute>} />
        <Route path="/user-profile" element={<ProtectedRoute><Layout><UserProfile /></Layout></ProtectedRoute>} />
        <Route path="/transcript" element={<ProtectedRoute><Layout><Transcript /></Layout></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Layout><Users /></Layout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
        <Route
          path="/community"
          element={
            enableCommunity ? (
              <Layout><Community /></Layout>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/community/posts/:postId"
          element={
            enableCommunity ? (
              <Layout><Community /></Layout>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    <ProfileLocationPrompt />
    </ErrorBoundary>
  )
}

export default App
